/**
 * Global error event emitter for handling authentication errors
 *
 * This module provides a simple event system for emitting and listening to
 * session expiration events that can occur anywhere in the application.
 */

import { reportError } from '@/lib/error-reporting'

type SessionExpiredCallback = () => void

class AuthErrorEmitter {
  private listeners: Set<SessionExpiredCallback> = new Set()

  /**
   * Register a listener for session expiration events
   */
  on(callback: SessionExpiredCallback): () => void {
    this.listeners.add(callback)

    // Return unsubscribe function
    return () => {
      this.listeners.delete(callback)
    }
  }

  /**
   * Emit a session expired event to all listeners
   */
  emit(): void {
    this.listeners.forEach(callback => {
      try {
        callback()
      } catch (error) {
        reportError(error, { where: 'auth-error-handler.AuthErrorEmitter.emit' })
      }
    })
  }

  /**
   * Clear all listeners
   */
  clear(): void {
    this.listeners.clear()
  }
}

export const authErrorEmitter = new AuthErrorEmitter()

/**
 * Emit a session expired event
 * This should be called when a 401 error or token refresh failure is detected
 */
export function emitSessionExpired(): void {
  console.warn('[AuthError] Session expired event emitted')
  authErrorEmitter.emit()
}

/**
 * Listen for session expired events
 * Returns an unsubscribe function
 */
export function onSessionExpired(callback: SessionExpiredCallback): () => void {
  return authErrorEmitter.on(callback)
}
